import { useCallback, useEffect, useRef, useState } from 'react'

import { Item, Sale } from '@/interfaces/cash-register/Item'
import { useAuth } from '@/lib/auth'

type StoredItem = {
  amount: number
  unitPrice: number
  taxRate?: number
  productId?: string
  productName?: string
  sale?: Sale
  description?: string
  skuId?: string
  skuName?: string
}

const storageKey = (accountId: string) => `invo.cashRegister.ticket.${accountId}`

const toStored = (item: Item): StoredItem => ({
  amount: item.amount,
  unitPrice: item.unitPrice,
  taxRate: item.taxRate,
  productId: item.productId,
  productName: item.productName,
  sale: item.sale,
  description: item.description,
  skuId: item.skuId,
  skuName: item.skuName,
})

const fromStored = (s: StoredItem) =>
  new Item(s.amount, s.unitPrice, s.taxRate, s.productId, s.productName, s.sale, s.description, s.skuId, s.skuName)

/**
 * Rozpracovaná účtenka přežije obnovení stránky: ukládá se do localStorage pro aktuální účet.
 * Po zaplacení se zavolá `markPaid()`, záznam se smaže a do vymazání účtenky se už neukládá,
 * aby se po reloadu nenabídla k opětovnému prodeji.
 */
export function usePersistentTicket() {
  const { user } = useAuth({ tmp: 'CashRegister' })
  const accountId = user?.currentAccountId ? String(user.currentAccountId) : null
  const [items, setItemsState] = useState<Item[]>([])
  const loadedFor = useRef<string | null>(null)
  const paid = useRef(false)

  useEffect(() => {
    if (!accountId || loadedFor.current === accountId) return
    loadedFor.current = accountId
    try {
      const raw = localStorage.getItem(storageKey(accountId))
      const parsed = raw ? (JSON.parse(raw) as StoredItem[]) : []
      if (Array.isArray(parsed) && parsed.length > 0) setItemsState(parsed.map(fromStored))
    } catch {
      localStorage.removeItem(storageKey(accountId))
    }
  }, [accountId])

  const setItems = useCallback(
    (next: Item[]) => {
      setItemsState(next)
      if (next.length === 0) paid.current = false
      if (!accountId || paid.current) return
      if (next.length === 0) localStorage.removeItem(storageKey(accountId))
      else localStorage.setItem(storageKey(accountId), JSON.stringify(next.map(toStored)))
    },
    [accountId],
  )

  const markPaid = useCallback(() => {
    paid.current = true
    if (accountId) localStorage.removeItem(storageKey(accountId))
  }, [accountId])

  return { items, setItems, markPaid }
}
